// §4.4 Transfers — LIST (office half; the floor Receive screen is the next
// slice, D-048).
//
// Columns: code · from → to · status pill · ETA · age-in-transit, which turns
// AMBER once today is past the ETA (§4.4). The age is computed from business
// DATES in the facility's own local date (§9.8) — never a timestamp subtraction
// a timezone could flip.
//
// Two §4.4 filters are honestly absent and the spec row is amended in this PR:
//   * "lines" count — §5 row 31's list response is the transfer HEADER only
//     (`TransferListResponse` = Transfer[]); a count would need one detail read
//     per row. It appears on the detail screen instead.
//   * date filter — §5 row 31 exposes `status` / `from_facility_id` /
//     `to_facility_id` only. Filtering client-side over one page would silently
//     hide matching rows on later pages, which is worse than no filter.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import type { components } from "@assessly/api-client";
import { Button } from "../../../../components/button";
import { DataTable } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { ProductPicker } from "../../catalogue/components/product-picker";
import { CursorPager } from "../components/filters";
import { FacilityRef, InventoryStatus } from "../components/inventory-ui";
import {
  useCreateTransfer,
  useFacilities,
  useLots,
  useStockUnits,
  useTransfers,
  type TransfersQuery,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { inventoryErrorMessage } from "../lib/errors";
import { daysPastDate, formatBusinessDate, isOverdue, formatMeasureWithUom } from "../lib/measure";

/** In flight = dispatched or partially received. The ETA is a PROMISE only
 * while the stock is on a truck: a draft with a past ETA is not late, it has
 * not left. */
function isInFlight(transfer: { status: string }): boolean {
  return transfer.status === "dispatched" || transfer.status === "partially_received";
}

type S = components["schemas"];

const TRANSFER_STATUSES = [
  "draft",
  "dispatched",
  "partially_received",
  "received",
  "cancelled",
] as const;

const PAGE_SIZE = 50;

export function TransfersListScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();

  // §4.9's in-transit drill link arrives as ?status=dispatched.
  const search = useSearch({ strict: false });
  // `strict: false` merges every route's search types, so `status` is a union
  // with other areas' params (production's is a multi-status array). Only this
  // route's own string form is meaningful here; anything else is ignored.
  const [status, setStatus] = useState(typeof search.status === "string" ? search.status : "");
  const [fromFacility, setFromFacility] = useState("");
  const [toFacility, setToFacility] = useState("");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [createOpen, setCreateOpen] = useState(false);
  const cursor = cursors[cursors.length - 1];

  const perms = useInventoryPermissions();
  const canCreate = perms.has("inventory.transfer.manage");
  const facilities = useFacilities();
  const facilityName = useMemo(() => {
    const map = new Map<string, string>();
    for (const facility of facilities.data?.items ?? []) map.set(facility.id, facility.name);
    return map;
  }, [facilities.data]);

  const query = useMemo(() => {
    const next: TransfersQuery = { limit: PAGE_SIZE };
    if (cursor !== undefined) next.cursor = cursor;
    if (status.length > 0) next.status = status;
    if (fromFacility.length > 0) next.from_facility_id = fromFacility;
    if (toFacility.length > 0) next.to_facility_id = toFacility;
    return next;
  }, [cursor, status, fromFacility, toFacility]);

  const transfers = useTransfers(query);

  useEffect(() => {
    if (transfers.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [transfers.error, navigate]);

  // "Nothing matches these filters" and "there are no transfers yet" are
  // different facts, and only the second one deserves a create CTA.
  const filtered = status.length > 0 || fromFacility.length > 0 || toFacility.length > 0;

  return (
    <section className="transfers-list">
      <PageHeader
        title={term("transfer")}
        actions={
          <>
            {canCreate ? (
              <Button
                variant="primary"
                onClick={() => {
                  setCreateOpen((prev) => !prev);
                }}
              >
                {t("inventory.transfers.new")}
              </Button>
            ) : null}
          </>
        }
      />

      {createOpen && canCreate ? (
        <TransferCreatePanel
          onClose={() => {
            setCreateOpen(false);
          }}
        />
      ) : null}

      <div className="filter-bar">
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.transfers.status")}</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setCursors([undefined]);
            }}
          >
            <option value="">{t("inventory.transfers.allStatuses")}</option>
            {TRANSFER_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`inventory.vocab.transfer.${value}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.transfers.from")}</span>
          <select
            value={fromFacility}
            onChange={(event) => {
              setFromFacility(event.target.value);
              setCursors([undefined]);
            }}
          >
            <option value="">{t("inventory.filters.allFacilities")}</option>
            {(facilities.data?.items ?? []).map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-chip">
          <span className="filter-chip-label">{t("inventory.transfers.to")}</span>
          <select
            value={toFacility}
            onChange={(event) => {
              setToFacility(event.target.value);
              setCursors([undefined]);
            }}
          >
            <option value="">{t("inventory.filters.allFacilities")}</option>
            {(facilities.data?.items ?? []).map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {transfers.isPending ? (
        <TableSkeleton rows={6} cols={5} label={t("inventory.transfers.loading")} />
      ) : null}

      {transfers.error && !(transfers.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.transfers.errorTitle")}
          detail={transfers.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void transfers.refetch()}
          requestId={requestIdOf(transfers.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {transfers.data?.items.length === 0 ? (
        <EmptyState
          title={
            filtered
              ? t("inventory.transfers.emptyFilteredTitle")
              : t("inventory.transfers.emptyTitle")
          }
          body={
            filtered
              ? t("inventory.transfers.emptyFilteredBody")
              : t("inventory.transfers.emptyBody")
          }
          {...(!filtered && canCreate
            ? {
                ctaLabel: t("inventory.transfers.new"),
                onCta: () => {
                  setCreateOpen(true);
                },
              }
            : {})}
        />
      ) : null}

      {transfers.data !== undefined && transfers.data.items.length > 0 ? (
        <>
          <DataTable
            columns={[
              {
                key: "transfer_code",
                header: t("inventory.transfers.colCode"),
                id: true,
                render: (transfer) => (
                  <Link
                    to="/inventory/transfers/$id"
                    params={{ id: transfer.id }}
                    className="ds-lnk"
                  >
                    {transfer.transfer_code}
                  </Link>
                ),
              },
              {
                key: "route",
                header: t("inventory.transfers.colRoute"),
                render: (transfer) => (
                  <>
                    <FacilityRef
                      id={transfer.from_facility_id}
                      name={facilityName.get(transfer.from_facility_id)}
                      pending={facilities.isPending}
                    />
                    {" → "}
                    <FacilityRef
                      id={transfer.to_facility_id}
                      name={facilityName.get(transfer.to_facility_id)}
                      pending={facilities.isPending}
                    />
                  </>
                ),
              },
              {
                key: "status",
                header: t("inventory.transfers.colStatus"),
                render: (transfer) => <InventoryStatus status={transfer.status} scope="transfer" />,
              },
              {
                key: "eta_on",
                header: t("inventory.transfers.colEta"),
                render: (transfer) =>
                  transfer.eta_on === null
                    ? t("inventory.transfers.noEta")
                    : formatBusinessDate(transfer.eta_on, i18n.language),
              },
              {
                key: "age",
                header: t("inventory.transfers.colAge"),
                age: true,
                render: (transfer) =>
                  !isInFlight(transfer) ? (
                    "—"
                  ) : isOverdue(transfer.eta_on) ? (
                    <span className="status-badge status-warning" data-testid="age-overdue">
                      {t("inventory.transfers.daysLate", { n: daysPastDate(transfer.eta_on) })}
                    </span>
                  ) : (
                    t("inventory.transfers.onTime")
                  ),
              },
            ]}
            rows={transfers.data.items}
            rowKey={(transfer) => transfer.id}
            empty={null}
            rowClassName={(transfer) =>
              isInFlight(transfer) && isOverdue(transfer.eta_on) ? "row-overdue" : undefined
            }
          />
          <CursorPager
            canPrev={cursors.length > 1}
            nextCursor={transfers.data.next_cursor}
            onPrev={() => {
              setCursors((prev) => prev.slice(0, -1));
            }}
            onNext={(next) => {
              setCursors((prev) => [...prev, next]);
            }}
          />
        </>
      ) : null}
    </section>
  );
}

/** How many batches the per-line lot picker offers (newest first). */
const LOT_OPTIONS = 50;

interface LineDraft {
  readonly key: number;
  serial: string;
  stockUnitId: string;
  productId: string;
  /** §3.3: a bulk line dispatches from ONE level row — the lot names it. */
  lotId: string;
  quantity: string;
  uom: string;
}

/**
 * §4.4's "storekeeper/procurement/planner CREATE" — POST /transfers (§5 row
 * 30b). Without it the office could only view and dispatch transfers a seed or
 * an agent had made, so a planner moving twelve bobbins from Almaty to
 * Shymkent had no way in at all.
 *
 * A line is a UNIT (the KazKabel case: a serial, whose product/uom/remaining
 * come from the unit itself) or a bulk quantity of a product. The unit path
 * resolves the serial through §5 row 12's `serial_q` rather than asking anyone
 * to paste a uuid.
 */
function TransferCreatePanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const create = useCreateTransfer();
  const facilities = useFacilities();
  const facilityItems = facilities.data?.items ?? [];

  const [fromFacility, setFromFacility] = useState("");
  const [toFacility, setToFacility] = useState("");
  const [eta, setEta] = useState("");
  const [carrier, setCarrier] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([
    { key: 1, serial: "", stockUnitId: "", productId: "", lotId: "", quantity: "", uom: "" },
  ]);

  // A line the SERVER will refuse must not be offered as creatable. §5 row 30b
  // requires every line to name a product or a stock unit (422
  // TRANSFER_STATE_INVALID, transfers.service.ts), so a line with only a
  // quantity and a UoM is not "a bare-container line" — it is a request this
  // screen knows will bounce. The gate names the missing half rather than
  // greying the button silently.
  const filled = lines.filter(
    (line) => line.quantity.trim().length > 0 && line.uom.trim().length > 0,
  );
  const unnamed = filled.filter(
    (line) => line.stockUnitId.length === 0 && line.productId.length === 0,
  );
  const blocker =
    fromFacility.length === 0 || toFacility.length === 0
      ? t("inventory.transfers.needFacilities")
      : fromFacility === toFacility
        ? t("inventory.transfers.needDistinctFacilities")
        : filled.length === 0
          ? t("inventory.transfers.needLine")
          : unnamed.length > 0
            ? t("inventory.transfers.needLineSubject")
            : null;
  const ready = blocker === null;

  if (create.isSuccess) {
    return (
      <section className="inv-panel" aria-label={t("inventory.transfers.new")}>
        <p className="inv-success" role="status">
          {t("inventory.transfers.created", { code: create.data.transfer.transfer_code })}
        </p>
        <div className="form-actions">
          <Link
            to="/inventory/transfers/$id"
            params={{ id: create.data.transfer.id }}
            className="ds-lnk"
          >
            {t("inventory.transfers.openCreated")}
          </Link>
          <Button onClick={onClose}>{t("inventory.common.close")}</Button>
        </div>
      </section>
    );
  }

  return (
    <section className="inv-panel" aria-label={t("inventory.transfers.new")}>
      <div className="inv-panel-head">
        <h2>{t("inventory.transfers.new")}</h2>
        <Button className="inv-panel-close" onClick={onClose}>
          {t("inventory.common.close")}
        </Button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          const body: S["TransferCreateRequest"] = {
            from_facility_id: fromFacility,
            to_facility_id: toFacility,
            lines: lines
              .filter((line) => line.quantity.trim().length > 0 && line.uom.trim().length > 0)
              .map((line) => ({
                quantity: line.quantity.trim(),
                uom: line.uom.trim(),
                ...(line.stockUnitId.length > 0 ? { stock_unit_id: line.stockUnitId } : {}),
                ...(line.productId.length > 0 ? { product_id: line.productId } : {}),
                ...(line.stockUnitId.length === 0 && line.lotId.length > 0
                  ? { lot_id: line.lotId }
                  : {}),
              })),
          };
          if (eta.length > 0) body.eta_on = eta;
          if (carrier.trim().length > 0) body.carrier = carrier.trim();
          create.mutate(body);
        }}
      >
        <Field label={t("inventory.transfers.from")}>
          <Select
            value={fromFacility}
            onChange={(event) => {
              setFromFacility(event.target.value);
            }}
            required
          >
            <option value="">{t("inventory.transfers.pickFacility")}</option>
            {facilityItems.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("inventory.transfers.to")}>
          <Select
            value={toFacility}
            onChange={(event) => {
              setToFacility(event.target.value);
            }}
            required
          >
            <option value="">{t("inventory.transfers.pickFacility")}</option>
            {facilityItems
              .filter((facility) => facility.id !== fromFacility)
              .map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field label={t("inventory.transfers.colEta")}>
          <input
            type="date"
            className="ds-input"
            value={eta}
            onChange={(event) => {
              setEta(event.target.value);
            }}
          />
        </Field>
        <Field label={t("inventory.transferDetail.carrier")}>
          <input
            className="ds-input"
            value={carrier}
            onChange={(event) => {
              setCarrier(event.target.value);
            }}
          />
        </Field>

        <h3>{t("inventory.transferDetail.lines")}</h3>
        {lines.map((line) => (
          <TransferLineRow
            key={line.key}
            line={line}
            facilityId={fromFacility}
            locale={i18n.language}
            canRemove={lines.length > 1}
            onChange={(next) => {
              setLines((prev) => prev.map((item) => (item.key === line.key ? next : item)));
            }}
            onRemove={() => {
              setLines((prev) => prev.filter((item) => item.key !== line.key));
            }}
          />
        ))}
        <Button
          onClick={() => {
            setLines((prev) => [
              ...prev,
              {
                key: (prev[prev.length - 1]?.key ?? 0) + 1,
                serial: "",
                stockUnitId: "",
                productId: "",
                lotId: "",
                quantity: "",
                uom: "",
              },
            ]);
          }}
        >
          {t("inventory.transfers.addLine")}
        </Button>

        {create.isError ? (
          <p className="form-error" role="alert">
            {inventoryErrorMessage(t, create.error, "inventory.transfers.createFailed")}
          </p>
        ) : null}
        {blocker !== null ? (
          <p className="inv-hint" data-testid="transfer-create-blocked">
            {blocker}
          </p>
        ) : null}
        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            busy={create.isPending}
            busyLabel={t("inventory.transfers.creating")}
            disabled={!ready}
          >
            {t("inventory.transfers.createSubmit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

function TransferLineRow({
  line,
  facilityId,
  locale,
  canRemove,
  onChange,
  onRemove,
}: {
  line: LineDraft;
  facilityId: string;
  locale: string;
  canRemove: boolean;
  onChange: (next: LineDraft) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const debounced = useDebouncedValue(line.serial, 250);
  // The lot list is meaningless without a product, so the read is not issued
  // until there is one — the `{}` fallback was an unfiltered tenant-wide lots
  // query fired for every empty line and used by nothing.
  const lots = useLots(
    { product_id: line.productId, limit: LOT_OPTIONS },
    line.productId.length > 0,
  );
  const matches = useStockUnits(
    {
      serial_q: debounced.trim(),
      limit: 10,
      ...(facilityId.length > 0 ? { facility_id: facilityId } : {}),
    },
    debounced.trim().length > 0 && line.stockUnitId.length === 0,
  );

  return (
    <div className="transfer-line-draft">
      <Field label={t("inventory.transfers.lineSerial")}>
        <input
          className="ds-input"
          value={line.serial}
          onChange={(event) => {
            onChange({ ...line, serial: event.target.value, stockUnitId: "" });
          }}
          placeholder={t("inventory.movements.serialPlaceholder")}
        />
      </Field>
      {(matches.data?.items ?? []).length > 0 && line.stockUnitId.length === 0 ? (
        <ul className="picker-results">
          {(matches.data?.items ?? []).map((unit) => (
            <li key={unit.id}>
              <button
                type="button"
                onClick={() => {
                  // The unit dictates product, UoM and the quantity on it —
                  // typing those by hand is how a transfer line ends up naming
                  // a different product than the bobbin it moves.
                  onChange({
                    ...line,
                    serial: unit.serial_code,
                    stockUnitId: unit.id,
                    productId: unit.product_id ?? "",
                    lotId: "",
                    quantity: unit.remaining_measure ?? "",
                    uom: unit.measure_uom ?? "",
                  });
                }}
              >
                {unit.serial_code} ·{" "}
                {formatMeasureWithUom(unit.remaining_measure, unit.measure_uom, { locale })}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {/* §4.4: "a line names a UNIT by serial … OR A BULK QUANTITY OF A
          PRODUCT". The product half was missing (found by M06-S05's sibling
          M06-S06, 2026-08-02): a bulk line could only be created with a
          quantity and a UoM and NO product — which the SERVER REFUSES with 422
          TRANSFER_STATE_INVALID ("a line names a product or a stock unit",
          transfers.service.ts), so the pellet/rod transfer this screen exists
          for could not be authored here at all. (An earlier version of this
          comment claimed such a line "dispatches as a bare-container line". It
          does not; it bounces. Corrected 2026-08-02 — and `ready` now refuses
          it here, so the panel never offers Create for a request it knows the
          server will reject.) A line that
          named a unit keeps taking its product FROM the unit; the picker is
          disabled there rather than hidden, so the two shapes read as one
          control. */}
      <div className="inv-label">
        <span>{t("inventory.transfers.lineProduct")}</span>
        <ProductPicker
          value={line.productId}
          onChange={(productId) => {
            onChange({ ...line, productId, lotId: "" });
          }}
          placeholder={t("inventory.transfers.linePickProduct")}
          disabled={line.stockUnitId.length > 0}
        />
        {line.stockUnitId.length > 0 ? (
          <p className="inv-hint">{t("inventory.transfers.lineProductFromUnit")}</p>
        ) : null}
      </div>
      {/* …AND ITS LOT. §3.3's draft validation reads the EXACT level row the
          dispatch will decrement, and a lot-less line "never borrows cover from
          lot-carrying rows" — while EVERY receipt creates or adopts a lot
          (§5 row 27 requires one). A lot-less bulk line therefore found 0 on
          hand and could never dispatch, which made this control unusable for
          the pellet transfer it exists for (found by M06-S06, 2026-08-02). The
          empty option stays: bare-container and genuinely lot-less stock is a
          real shape, and it is now a CHOICE rather than the only outcome. */}
      {line.stockUnitId.length === 0 && line.productId.length > 0 ? (
        <Field label={t("inventory.transfers.lineLot")}>
          <Select
            value={line.lotId}
            onChange={(event) => {
              onChange({ ...line, lotId: event.target.value });
            }}
            data-testid="transfer-line-lot"
          >
            <option value="">{t("inventory.transfers.lineLotAny")}</option>
            {(lots.data?.items ?? []).map((lot) => (
              <option key={lot.id} value={lot.id}>
                {lot.lot_code}
              </option>
            ))}
          </Select>
          {/* The list is the NEWEST batches (the API orders `created_at DESC`)
              and it is capped. A product with more batches than the cap would
              otherwise present a silently truncated picker in which an older
              batch simply does not exist — so the truncation is stated, with
              where to go instead. */}
          {lots.data?.next_cursor != null ? (
            <span className="inv-hint">
              {t("inventory.transfers.lineLotTruncated", { n: LOT_OPTIONS })}
            </span>
          ) : null}
        </Field>
      ) : null}
      <Field label={t("inventory.transferDetail.colQty")}>
        <input
          className="ds-input"
          value={line.quantity}
          inputMode="decimal"
          onChange={(event) => {
            onChange({ ...line, quantity: event.target.value });
          }}
          required
        />
      </Field>
      <Field label={t("inventory.transfers.lineUom")}>
        <input
          className="ds-input"
          value={line.uom}
          onChange={(event) => {
            onChange({ ...line, uom: event.target.value });
          }}
          required
        />
      </Field>
      {canRemove ? <Button onClick={onRemove}>{t("inventory.common.remove")}</Button> : null}
    </div>
  );
}
